import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "combine-order-modal" }
const _hoisted_2 = { class: "modal-main" }
const _hoisted_3 = { class: "summary" }
const _hoisted_4 = { class: "summary__container summary__container--title" }
const _hoisted_5 = { class: "summary__text-bold" }
const _hoisted_6 = { class: "" }
const _hoisted_7 = { class: "summary__container summary__container--value" }
const _hoisted_8 = { class: "" }
const _hoisted_9 = { class: "summary__text-bold" }
const _hoisted_10 = { class: "summary__container summary__container--value" }
const _hoisted_11 = { class: "" }
const _hoisted_12 = { class: "summary__text-bold" }
const _hoisted_13 = { class: "summary__container summary__container--value" }
const _hoisted_14 = { class: "" }
const _hoisted_15 = { class: "summary__text-bold" }
const _hoisted_16 = { class: "summary__container summary__container--value" }
const _hoisted_17 = { class: "" }
const _hoisted_18 = { class: "control" }
const _hoisted_19 = { class: "control__error" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { key: 0 }

import TextButton from '@/components/input/Btn/TextButton.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import TopSlideDownModal from '@/corePagesModule/components/TopSlideDownModal.vue';
import { i18n } from '@/i18n';
import { ICombinedOrder, ICombinedOrderWithBusinessAndBasis } from '@/InventoryControlModule/domains/entities/InventoryOrder';
import { useInventoryStore } from '@/InventoryControlModule/pages/useInventoryStore';
import SvgLoader from '@/utils/SvgLoader.vue';
import { storeToRefs } from 'pinia';
import { computed, ref, StyleValue, watch } from 'vue';
import { MAX_DISPLAY_COMBINED_ORDER_ITEMS, MAX_DISPLAY_COMBINED_ORDER_ERROR_MESSAGE } from '@/InventoryControlModule/constants/index';
import { ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { useForms } from '@/corePagesModule/composables/useForm';
import { OrderBasisFactory } from '@/coreDomainsModule/factories/OrderBasisFactory';
import { OrderBasisService } from '@/coreDomainsModule/services/orderBasisService';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { CombinedOrderService } from '@/InventoryControlModule/domains/services/combinedOrderService';


export default /*@__PURE__*/_defineComponent({
  __name: 'CombineOrderModal',
  setup(__props) {

const t = i18n.t;

const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const { toggleCheckedRowData, createDefaultOrderGroupTitle } = inventoryStore;

const isOpen = ref<boolean>(false);
const fieldItems = [
  {
    name: 'maxTotalAmount',
    rules: [ValidateRule.Required, ValidateRule.NegativeNumber],
  },
];

const { fieldState, allValidate, resetForm } = useForms(fieldItems);
watch(
  () => state.value.checkedAggregateBusinessIds,
  (val) => {
    if (val.length >= 2) {
      isOpen.value = true;
      if (state.value.combinedOrdersState.combinedOrderContents.maxTotalAmount) {
        fieldState['maxTotalAmount'].value.value = state.value.combinedOrdersState.combinedOrderContents.maxTotalAmount.toString();
      }
      allValidate();
    } else {
      if (isOpen.value) closeModal();
    }
  }
);

const numberMaxTotalAmount = computed(() => {
  return parseFloat(fieldState['maxTotalAmount'].value.value) ?? 0;
});

const selectedBusinessItems = computed(() => {
  return state.value.businessList.filter((business) => state.value.checkedAggregateBusinessIds.includes(business.businessId));
});

const calcedOrders = computed((): ICombinedOrder[] => {
  const combinedBusinessOrders = state.value.checkedAggregateBusinessIds.map((businessId) => {
    return { businessId: businessId, orders: Object.values(state.value.orderEditData[businessId]) };
  });
  return combinedBusinessOrders;
});

const calcedOrderTotalAmount = computed(() => {
  const targetMonthDiff = DateService.calculateDiffMonths(new Date(), DateService.convertIDateToDate(targetDate.value));
  return CombinedOrderService.quantize(
    calcedOrders.value.reduce((acc, order) => {
      const amount = order.orders[targetMonthDiff]?.amount;
      return acc + (!amount || isNaN(amount) ? 0 : amount);
    }, 0)
  );
});

const calcedOrderLoadFactor = computed(() => {
  if (isNaN(calcedOrderTotalAmount.value) || isNaN(numberMaxTotalAmount.value)) return 0;
  const val = (calcedOrderTotalAmount.value / numberMaxTotalAmount.value) * 100;
  return isFinite(val) ? val : 0;
});

const targetDate = computed(() => {
  const flatCalcedOrder = calcedOrders.value.map((order) => order.orders).flat();
  const earlyestDate = flatCalcedOrder.reduce(
    (earlyest, current) => {
      if (isNaN(current.amount) || current.amount === 0 || !current.crossDate) return earlyest;
      if (!earlyest) return current.crossDate;
      const currentDate = DateService.convertIDateToDate(current.crossDate);
      const earlyestDate = DateService.convertIDateToDate(earlyest);
      return currentDate < earlyestDate ? current.crossDate : earlyest;
    },
    { year: 9999, month: 12, day: 31 }
  );
  if (!earlyestDate) return DateService.convertDateToIDate(new Date());
  return earlyestDate;
});

const combinedOrders = computed((): ICombinedOrder[] => {
  return calcedOrders.value.map((combinedOrder) => {
    const filteredOrder = combinedOrder.orders.filter((order) => {
      return !isNaN(order.amount) && order.crossDate && DateService.compareDates(order.crossDate, targetDate.value) === 0;
    });
    return { businessId: combinedOrder.businessId, orders: filteredOrder };
  });
});

const errorMessage = computed(() => {
  const errMsgArray: string[] = [];
  if (!fieldState['maxTotalAmount'] || fieldState['maxTotalAmount'].isError.value) errMsgArray.push(t('上限数量が入力されていません'));
  if (numberMaxTotalAmount.value < calcedOrderTotalAmount.value) errMsgArray.push(t('合計数量が上限数を超えています'));
  if (!combinedOrders.value.every((combinedOrder) => combinedOrder.orders.length !== 0)) {
    errMsgArray.push(t('まとめ発注では全商品の納期を同じ日にする必要があります'));
  } else if (!combinedOrders.value.every((combinedOrder) => combinedOrder.orders.length === 1)) {
    errMsgArray.push(t('1つの取引の中に同じ希望納期の発注が2つ以上存在します'));
  }
  return errMsgArray.slice(0, MAX_DISPLAY_COMBINED_ORDER_ERROR_MESSAGE);
});
//['まとめ発注では全商品の納期を同じ日にする必要があります', '合計数量が上限数を超えています'];

const selectedBusinessStyle = computed((): StyleValue => {
  return {
    gridTemplateColumns: `auto repeat(${Math.min(
      state.value.checkedAggregateBusinessIds.length - 1,
      MAX_DISPLAY_COMBINED_ORDER_ITEMS
    )}, auto) auto`,
  };
});

/**
 * モーダルを閉じる
 */
const closeModal = () => {
  isOpen.value = false;
  resetForm(fieldItems);
};

/**
 * まとめ発注作成ボタンを押した際の処理
 */
const onNext = async () => {
  const confirmedOrdersWithBusinessAndBasis: Array<ICombinedOrderWithBusinessAndBasis | undefined> = combinedOrders.value.map(
    (combinedOrder) => {
      const orderBusiness = selectedBusinessItems.value.find((business) => business.businessId === combinedOrder.businessId);
      if (!orderBusiness || combinedOrder.orders.length !== 1) return undefined;
      const createdOrderBasisInfo = OrderBasisFactory.create({
        snapshotLatestInventoryAmount: orderBusiness.inventory,
        snapshotExecutionDate: orderBusiness.executionDate?.getTime(),
        snapshotPackage: Number(orderBusiness.package),
        snapshotMoq: orderBusiness.MOQ,
        userAdjustedLeadTime:
          orderBusiness.leadTime && orderBusiness.leadTimeUnit
            ? { value: orderBusiness.leadTime, unit: orderBusiness.leadTimeUnit }
            : undefined,
        userAdjustedStdInventoryIndex: orderBusiness.stdInventoryIndices?.['標準在庫指数'],
        nextMonthlyInfos: OrderBasisService.getNextMonthlyInfosForCreateOrder(
          orderBusiness.customerAnnounce ?? [],
          (orderBusiness.customerAnnounce ?? []).map((cf) => ({
            ...cf,
            amount: (cf.amount * (state.value.forecastAccuracyData[orderBusiness.businessId]?.settingAccuracy || 100)) / 100,
          })),
          orderBusiness.recommend,
          orderBusiness,
          combinedOrder.orders.slice(0, 1).map((order) => ({ ...order, status: OrderStatus.ApprovedAwaitingDeliveryDate }))
        ),
        pastMonthlyInfos: OrderBasisService.getPastMonthlyInfosForCreateOrder(
          orderBusiness.customerAnnounce ?? [],
          orderBusiness.salesResults ?? []
        ),
      });
      return {
        business: orderBusiness,
        order: combinedOrder.orders[0],
        basis: createdOrderBasisInfo,
      };
    }
  );
  if (!confirmedOrdersWithBusinessAndBasis.every((order) => order !== undefined)) {
    // 取引情報がなぜかないとき
    showInfoModal({
      modalTitle: t('エラーが発生しました'),
      modalDescription: t('リロードして再度お試しください'),
      backButtonLabel: t('確認'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }

  const tempGroupName = await createDefaultOrderGroupTitle(confirmedOrdersWithBusinessAndBasis.length);

  state.value.combinedOrdersState.combinedOrderContents = {
    groupName: tempGroupName,
    orderCount: confirmedOrdersWithBusinessAndBasis.length,
    totalAmount: calcedOrderTotalAmount.value,
    maxTotalAmount: numberMaxTotalAmount.value,
    desiredDeliveryDate: targetDate.value,
    loadFactor: calcedOrderLoadFactor.value,
    confirmedOrdersWithBusinessAndBasis: confirmedOrdersWithBusinessAndBasis,
  };
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(TopSlideDownModal, {
    "is-open": isOpen.value,
    "z-index": 20,
    style: { padding: '16px' }
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(SvgLoader, { name: "shoppingCart" }),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)('まとめ発注選択中の件数', { number: _unref(state).checkedAggregateBusinessIds.length })), 1),
              _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)('まとめ発注モード')), 1)
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)('発注合計数量')), 1),
              _createElementVNode("div", _hoisted_9, _toDisplayString(calcedOrderTotalAmount.value.addComma()), 1)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)('希望納期')), 1),
              _createElementVNode("div", _hoisted_12, _toDisplayString(`${_unref(DateService).convertIYearMonthDayToYYYYMMDDWithSlash(targetDate.value, true)}`), 1)
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(t)('積載率')), 1),
              _createElementVNode("div", _hoisted_15, _toDisplayString(calcedOrderLoadFactor.value.roundToDecimal(2)) + "%", 1)
            ]),
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("div", _hoisted_17, _toDisplayString(_unref(t)('上限数量')), 1),
              _createVNode(NumberInputWithComma, {
                "model-value": numberMaxTotalAmount.value,
                class: "summary__input",
                onInput: _cache[0] || (_cache[0] = 
                (val) => {
                  _unref(fieldState)['maxTotalAmount'].setValue(val, true);
                }
              )
              }, null, 8, ["model-value"])
            ])
          ]),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("div", _hoisted_19, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errorMessage.value, (msg, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "control__error-row"
                }, [
                  _createVNode(SvgLoader, { name: "alertCellError" }),
                  _createTextVNode(" " + _toDisplayString(_unref(t)(msg)), 1)
                ]))
              }), 128))
            ]),
            _createVNode(TextButton, {
              disabled: !!errorMessage.value.length,
              onClick: onNext
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('まとめ発注作成')), 1)
              ], undefined, true),
              _: 1
            }, 8, ["disabled"]),
            _createElementVNode("button", { onClick: closeModal }, [
              _createVNode(SvgLoader, { name: "closeBoldBlue" })
            ])
          ])
        ]),
        _createElementVNode("div", {
          class: "modal-selected",
          style: _normalizeStyle(selectedBusinessStyle.value)
        }, [
          _createTextVNode(_toDisplayString(_unref(t)('選択商品：')) + " ", 1),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedBusinessItems.value.slice(0, _unref(MAX_DISPLAY_COMBINED_ORDER_ITEMS)), (businessItem, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "selected-item"
            }, [
              _createTextVNode(_toDisplayString(businessItem.businessNumber) + " / " + _toDisplayString(businessItem.businessName) + " ", 1),
              _createElementVNode("button", {
                onClick: ($event: any) => (_unref(toggleCheckedRowData)(businessItem, false))
              }, [
                _createVNode(SvgLoader, { name: "close" })
              ], 8, _hoisted_20)
            ]))
          }), 128)),
          (_unref(state).checkedAggregateBusinessIds.length > _unref(MAX_DISPLAY_COMBINED_ORDER_ITEMS))
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(_unref(t)('他n件', { number: _unref(state).checkedAggregateBusinessIds.length - _unref(MAX_DISPLAY_COMBINED_ORDER_ITEMS) })), 1))
            : _createCommentVNode("", true)
        ], 4)
      ])
    ], undefined, true),
    _: 1
  }, 8, ["is-open"]))
}
}

})